import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  ButtonType,
  Tooltip
} from 'syngenta-digital-cropwise-react-ui-kit';
import { useFormikContext } from 'formik';
import { useLocation } from 'react-router-dom';

import PlusIcon from 'assets/plus.svg?react';
import NoProductsSelectedIcon from 'assets/no_products_found.svg';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import DrawerDimmingOverlay from 'components/DrawerDimmingOverlay';
import EmptyTableState from 'components/EmptyTableState';
import FormSection from 'components/FormSection';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import AddProductDrawer from 'screens/ProductList/components/AddProductDrawer';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import ProductListProvider from 'screens/ProductList/context/ProductListProvider';
import FormToggle from 'components/FormToggle';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import { Context } from 'components/Store';
import { Maths } from 'helpers/maths';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import {
  Flow,
  RoutePathState
} from 'screens/Applications&WorkOrders/RouteAdmin';
import { datadogLogs } from '@datadog/browser-logs';
import useHandlePreferencesErrorToast from 'hooks/useHandlePreferencesErrorToast';
import FillProductDetailsDrawer from './components/FillProductDetailsDrawer';
import { ProductInventoryItem } from './components/FillProductDetailsDrawer/types';

import {
  formatProductsToApplication,
  updateProductList,
  recalculateProductsOnPropertiesChange
} from './helpers';
import TankInformationSection from './components/TankInformationSection';
import isTankInfoIncomplete from './helpers/isTankInfoIncomplete';
import CreateCustomProductDrawer from './components/CreateCustomProductDrawer';
import ProductsTable from './components/ProductsTable';
import { isLiquidWithTankInfo } from './components/ProductsTable/helpers';
import { ProductStepProvider } from './hooks/useProductStep';
import { QUANTITY_MODES } from './components/FillProductDetailsDrawer/constants';

// Ensure the drawer appears above other UI elements

const DRAWER_Z_INDEX = 1000;

const AddProductsButton: React.FC<{
  ghost?: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ ghost = false, onClick, disabled }) => (
  <Button
    style={{ background: ghost ? 'white' : undefined }}
    type={ButtonType.primary}
    icon={<PlusIcon fill={ghost ? '#14803C' : 'white'} />}
    onClick={onClick}
    disabled={disabled}
    ghost={ghost}
  >
    {LayoutStrings.products.selectProducts}
  </Button>
);

const ButtonWrapper = ({
  disabled,
  children
}: {
  disabled: boolean;
  children: React.ReactNode;
}) =>
  disabled ? (
    <Tooltip
      title={
        LayoutStrings.products.tankInfo.disabledSelectProductsButtonTooltip
      }
      placement="topRight"
      trigger="hover"
      color="#232630"
    >
      <div>{children}</div>
    </Tooltip>
  ) : (
    <div>{children}</div>
  );

const LearnMoreButton = () => (
  <a
    // Tailwind utilities now always outrank the UI kit's generic `a:hover`
    // rule (cascade layers ignore specificity), so restore its hover color
    // explicitly.
    className="px-2 text-blue-60 hover:text-[#21b8cc] inline-flex items-center"
    href="https://help.us.cropwise.com/hc/en-us/articles/50429909093779-Adding-Tank-Information"
    target="_blank"
    rel="noopener noreferrer"
  >
    <OpenInNewIcon className="mr-2" />
    {LayoutStrings.buttons.learnMore}
  </a>
);

const ProductsStep: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [
    isCreateCustomProductDrawerVisible,
    setIsCreateCustomProductDrawerVisible
  ] = useState(false);
  const [myProductsReloadTrigger, setMyProductsReloadTrigger] = useState(0);

  // Access utility context for both products and weather functionality

  // NOTE: Weather functionality is handled by the save button in the step itself
  // Weather auto-fetch and save logic is implemented in the parent component

  // NOTE: Product utilities are flexible and ready for future development
  // Current structure: utilityState.products = {} (empty object)
  //
  // Future usage examples:
  // updateProductUtilities({ autoCalculationEnabled: true });
  // updateProductUtilities({ calculationMode: 'advanced' });
  // updateProductUtilities({ autoValidation: false, customSettings: {...} });
  //
  // The structure will be defined based on actual product automation requirements

  const [
    {
      organization: { id: orgId }
    }
  ] = (useContext(Context) as unknown) as [
    Record<string, any>,
    React.Dispatch<any>
  ];

  const { state } = useLocation<RoutePathState | undefined>();

  const {
    values,
    setFieldValue,
    setFieldTouched,
    validateField,
    touched
  } = useFormikContext<FormValues>();

  const { utilityState, updateUtilityState } = useAgOperationUtility();
  const { displayPreferenceErrorToast } = useHandlePreferencesErrorToast();

  const skipTankPreferences =
    (state?.flow === Flow.DUPLICATE && state?.operation) ||
    state?.flow === Flow.FROM_WORK_ORDER_TO_APPLICATION;
  // If carrier is not present in products with tank info, we only want to load the carrier preference, not the full tank info preferences
  const shouldLoadCarrierPreferenceOnly =
    values.isTankInfoEnabled && !values.products?.some(p => p.isCarrier);

  // Initialize preferences hook
  const { findPreferenceByKey } = usePreferences();

  const applyTankPreferences = async ({
    carrierOnly = false,
    applyToggle = false
  } = {}) => {
    let tankPreference;
    try {
      tankPreference = await findPreferenceByKey(
        PreferenceKey.OPERATION_TANK_INFORMATION,
        { orgId }
      );
    } catch (error) {
      datadogLogs.logger.error('Error loading tank information preferences', {
        error,
        orgId
      });
      displayPreferenceErrorToast();
      return;
    }

    if (!tankPreference?.preferenceValue) return;

    const {
      toggleEnabled,
      tankSize,
      overallRatePerAcre,
      carrierId,
      isCarrierHidden
    } = tankPreference.preferenceValue;

    updateUtilityState({
      hasTankInfoPreferencesBeenLoaded: true,
      loadedUserPreferences: tankPreference.preferenceValue
    });

    if (carrierOnly) {
      if (carrierId) {
        updateUtilityState({ selectedCarrier: carrierId });
      }
      return;
    }

    setFieldValue('isCarrierRowHidden', isCarrierHidden ?? false);

    if (carrierId) {
      updateUtilityState({ selectedCarrier: carrierId });

      if (applyToggle) {
        setFieldValue('isTankInfoEnabled', toggleEnabled);
      }
      if (tankSize === 0 && overallRatePerAcre === 0) return;
      setFieldValue('tankSize', tankSize);
      setFieldValue('overallRatePerAcre', overallRatePerAcre);
      const calculatedTotalProduct = Maths.multiplyRound(
        overallRatePerAcre,
        values.totalAppliedAcreage
      );
      setFieldValue('totalProduct', calculatedTotalProduct);
      setFieldValue(
        'tankCount',
        Maths.divideSafeRound(calculatedTotalProduct, tankSize)
      );
    }
  };

  // Load tank information preferences when component mounts
  useEffect(() => {
    if (
      shouldLoadCarrierPreferenceOnly ||
      (utilityState.isEditMode !== undefined &&
        !utilityState.isEditMode &&
        !skipTankPreferences &&
        !utilityState.hasTankInfoPreferencesBeenLoaded)
    ) {
      applyTankPreferences({
        carrierOnly: shouldLoadCarrierPreferenceOnly,
        applyToggle: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilityState.isEditMode]);

  // Recalculate product values when area or tank settings change
  // Note: applicationMethod propagation is handled in AgOperationUtilityProvider
  // Skip the first fire of the recalculation effect. On mount, the products
  // already reflect the latest user-saved values (saved via FillProductDetailsDrawer
  // / EditCarrierDrawer / Step 2's handleTotalAcreageChange). Re-running
  // recalculateProductsOnPropertiesChange here would overwrite the user's exact
  // values with totalAppliedAcreage × coveragePercent / 100 — and if coveragePercent
  // has any floating-point drift (e.g. 100.0000000002), the user's saved
  // appliedAreaValue (e.g. 400) comes back as 400.0000000008 just from navigating
  // away and back. Subsequent fires (when a dependency actually changes) still run.
  const isInitialRecalcMount = useRef(true);
  useEffect(() => {
    if (isInitialRecalcMount.current) {
      isInitialRecalcMount.current = false;
      return;
    }
    if (!values.products?.length) return;
    // Don't recalculate if totalAppliedAcreage is not set yet - this would incorrectly zero out product values
    const modeCorrectedProducts = values.products.map(product => {
      const corrected = {
        ...product,
        quantityMode:
          product.quantityMode === QUANTITY_MODES.RATE_PER_TANK &&
          !values.isTankInfoEnabled
            ? 'ratePerAcre'
            : product.quantityMode
      };

      // When Tank Info is ON, force liquid products to 100% coverage.
      // Also force appliedAreaValue to match the new coverage so the corrected
      // product is internally consistent before it reaches
      // recalculateProductsOnPropertiesChange. Otherwise that helper — which
      // preserves appliedAreaValue when totalAppliedAcreage is unchanged —
      // would keep the stale value (computed against the pre-toggle coverage),
      // leaving the product with coverage=100 but appliedAreaValue ≠ total.
      if (
        isLiquidWithTankInfo(product.stdunit, values.isTankInfoEnabled) &&
        !product.isCarrier
      ) {
        corrected.coveragePercent = 100;
        corrected.appliedAreaValue = values.totalAppliedAcreage || 0;
      }

      return corrected;
    });

    setFieldValue(
      'products',
      recalculateProductsOnPropertiesChange(
        modeCorrectedProducts,
        values.totalAppliedAcreage,
        {
          tankCount: values.isTankInfoEnabled ? values.tankCount : undefined
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.totalAppliedAcreage,
    values.totalArea,
    values.tankCount,
    values.isTankInfoEnabled
  ]);

  const handleSelectProducts = () => {
    setIsDrawerOpen(true);
    setIsAddingProducts(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setIsAddingProducts(false);
  };

  useEffect(() => {
    setFieldTouched('products', true);
    validateField('products');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.products]);

  useEffect(() => {
    if (!touched.products && !utilityState.isEditMode && !state?.flow) {
      setIsDrawerOpen(true);
    }
  }, [touched.products, utilityState.isEditMode, state?.flow]);

  const isEmptyState =
    !values.isTankInfoEnabled &&
    (!values.products || values.products.length === 0);

  const [
    productUnderEdition,
    setProductUnderEdition
  ] = useState<Product | null>(null);
  const [isClosingEditDrawer, setIsClosingEditDrawer] = useState(false);
  // Tracks the pending `onCloseEditProductDrawer` unmount timer so a
  // newly-opened drawer can cancel a stale one from a previous close —
  // otherwise reopening within the 300ms window (e.g. save product A, then
  // immediately edit product B) lets the old timer null out `B`'s state out
  // from under the user.
  const pendingCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const cancelPendingClose = () => {
    if (pendingCloseTimeoutRef.current !== null) {
      clearTimeout(pendingCloseTimeoutRef.current);
      pendingCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => cancelPendingClose, []);

  const onCloseEditProductDrawer = () => {
    cancelPendingClose();
    if (isAddingProducts) {
      setIsDrawerOpen(true);
      pendingCloseTimeoutRef.current = setTimeout(() => {
        pendingCloseTimeoutRef.current = null;
        setProductUnderEdition(null);
      }, 300);
      return;
    }
    setIsClosingEditDrawer(true);
    pendingCloseTimeoutRef.current = setTimeout(() => {
      pendingCloseTimeoutRef.current = null;
      setProductUnderEdition(null);
      setIsClosingEditDrawer(false);
    }, 300);
  };

  const handleToggleChange = () => {
    setFieldValue('isTankInfoEnabled', !values.isTankInfoEnabled);
  };

  const toggleWithInfo = (
    <>
      <FormToggle
        data-testid="tank-information-toggle"
        checked={values.isTankInfoEnabled}
        onChange={handleToggleChange}
        toggleLabel={LayoutStrings.headers.tankInformation}
        checkedChildren="on"
        unCheckedChildren="off"
      />
      <LearnMoreButton />
    </>
  );

  const handleCustomProductCreationSuccess = (product: Product) => {
    cancelPendingClose();
    setIsCreateCustomProductDrawerVisible(false);
    setMyProductsReloadTrigger(prev => prev + 1);
    setProductUnderEdition(product);
    setIsAddingProducts(true);
  };

  const isAddProductsButtonDisabled = isTankInfoIncomplete({
    ...values,
    isCarrierRowHidden: false
  });

  return (
    <ProductStepProvider>
      {isEmptyState ? (
        <div className="flex flex-col w-full">
          <div className="flex flex-row mb-4">{toggleWithInfo}</div>
          <div className="bg-white shadow-sm-card rounded-lg">
            <EmptyTableState
              containerClassName="w-full h-full pt-6 pb-10 flex flex-col justify-center items-center"
              title={LayoutStrings.products.emptyStateTitle}
              message={LayoutStrings.products.emptyStateMessage}
              overlayIcon={NoProductsSelectedIcon}
              button={<AddProductsButton onClick={handleSelectProducts} />}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col w-full">
          <FormSection
            isOptional
            hasWrapper={false}
            headerContent={
              <div className="flex flex-row -ml-4">{toggleWithInfo}</div>
            }
            button={
              <ButtonWrapper disabled={isAddProductsButtonDisabled}>
                <AddProductsButton
                  disabled={isAddProductsButtonDisabled}
                  ghost
                  onClick={handleSelectProducts}
                />
              </ButtonWrapper>
            }
          >
            {/* Info banner for Recommendation -> Operation tank info */}
            {utilityState.showTankInfoBanner && (
              <Alert
                className="my-4 py-3 px-4 items-center"
                closeText={
                  <Button
                    type={ButtonType.outline}
                    size="small"
                    onClick={() =>
                      updateUtilityState({ showTankInfoBanner: false })
                    }
                  >
                    Dismiss
                  </Button>
                }
                message={null}
                description="Tank Information from your Recommendation is not carried over. You'll need to manually add the values to your Application."
                showIcon
                type="info"
              />
            )}
            {values.isTankInfoEnabled && (
              <>
                {/* Tank Information */}
                <div className="bg-white rounded-lg shadow-sm-card p-4 mb-10">
                  <TankInformationSection />
                </div>
              </>
            )}
            <ProductsTable
              onProductEdit={product => {
                cancelPendingClose();
                setProductUnderEdition(product);
                setIsAddingProducts(false);
              }}
            />
          </FormSection>
        </div>
      )}

      <DrawerDimmingOverlay
        visible={isDrawerOpen || !!(productUnderEdition && isAddingProducts)}
        zIndex={DRAWER_Z_INDEX - 2}
        onClick={
          isDrawerOpen && !productUnderEdition ? handleCloseDrawer : undefined
        }
      />

      <ProductListProvider>
        <AddProductDrawer
          showProductList
          hideBackButton
          zIndex={DRAWER_Z_INDEX}
          mask={false} // Dimming handled by persistent overlay above
          visible={isDrawerOpen}
          onClose={handleCloseDrawer}
          submitButtonText={LayoutStrings.products.closeDrawer}
          submitButtonType={ButtonType.outline}
          myProductsReloadTrigger={myProductsReloadTrigger}
          selectedProductsList={values.products.filter(
            (p: Product) => !p.isCarrier
          )}
          setSelectedProductsList={(items: Product[] = []) => {
            setFieldValue('products', items);
          }}
          handleOnEdit={(product: ProductInventoryItem | Product) => {
            cancelPendingClose();
            if ('isInUse' in product && product.isInUse) {
              setProductUnderEdition(product);
              setIsDrawerOpen(false);
              return;
            }

            const [formattedProduct] = formatProductsToApplication([
              product as ProductInventoryItem
            ]);
            setProductUnderEdition(formattedProduct);
            setIsAddingProducts(true);
            setIsDrawerOpen(false);
          }}
          handleAddProductsSubmit={handleCloseDrawer}
          handleOnAddCustomProduct={() => {
            setIsCreateCustomProductDrawerVisible(true);
            handleCloseDrawer();
          }}
        />

        {productUnderEdition && (
          <FillProductDetailsDrawer
            visible={!isClosingEditDrawer}
            isEditing={!isAddingProducts}
            zIndex={DRAWER_Z_INDEX - 1}
            suppressAnimation={isAddingProducts}
            mask={!isAddingProducts} // When adding from drawer, dimming handled by persistent overlay
            product={productUnderEdition}
            onClose={onCloseEditProductDrawer}
            onSave={(productToAdd: Product) => {
              // Enforce 100% coverage for liquid non-carrier products when Tank Info is ON
              const normalizedProduct =
                isLiquidWithTankInfo(
                  productToAdd.stdunit,
                  values.isTankInfoEnabled
                ) && !productToAdd.isCarrier
                  ? { ...productToAdd, coveragePercent: 100 }
                  : productToAdd;
              setFieldValue(
                'products',
                updateProductList(values?.products || [], normalizedProduct)
              );
              onCloseEditProductDrawer();
            }}
          />
        )}

        {isCreateCustomProductDrawerVisible && (
          <CreateCustomProductDrawer
            visible
            onClose={() => {
              setIsDrawerOpen(true);
              setIsCreateCustomProductDrawerVisible(false);
            }}
            onSuccess={handleCustomProductCreationSuccess}
            zIndex={DRAWER_Z_INDEX - 1}
          />
        )}
      </ProductListProvider>
    </ProductStepProvider>
  );
};

export default ProductsStep;
